/* Courier / client scorecard: one row each, worst first, with search, a status filter (needs attention / on track),
   a PoD-via filter for couriers, paging (there can be 100+ clients) and a CSV of what is shown.
   Clicking a row focuses the whole page on that courier / client, so the speedometers show its status. */
import React, { useEffect, useMemo, useState } from 'react'
import { Box, Button, Chip, IconButton, InputAdornment, LinearProgress, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow, TableSortLabel, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import { tableHeadSx } from '../../common/sharedStyles'
import { SOURCE_LABEL, TONE, bucketTone, num, pct, saveCsv } from './podUtils'
import { enter, growX } from './podMotion'

/** Flags for one row against the targets; text labels so status never relies on colour alone. */
export const rowFlags = (r, { receivedTargetPct, onTimeTargetPct, lastBucket }) => {
  const f = []
  if ((r.pendingByBucket?.[lastBucket] || 0) > 0) f.push({ label: 'Old pending', tone: 'error' })
  if (r.receivedPct != null && r.receivedPct < receivedTargetPct) f.push({ label: 'Below target', tone: 'orange' })
  if (r.onTimePct != null && r.onTimePct < onTimeTargetPct) f.push({ label: 'Late PoDs', tone: 'warning' })
  return f.length ? f : [{ label: 'On track', tone: 'success' }]
}

const FlagChip = ({ label, tone }) => (
  <Chip size='small' label={label} sx={{ bgcolor: TONE[tone].bg, color: TONE[tone].fg, border: 1, borderColor: TONE[tone].border, fontWeight: 600, height: 22,
    '& .MuiChip-label': { textTransform: 'none', px: 1 } }} />
)

/** Pending split by age as one small bar; counts in the tooltip and the label. */
const AgeBar = ({ counts = [], labels = [] }) => {
  const total = counts.reduce((s, n) => s + (n || 0), 0)
  if (!total) return <Typography component='span' sx={{ fontSize: 13, color: 'text.disabled' }}>–</Typography>
  const text = labels.map((l, i) => `${l}: ${counts[i] || 0}`).join(' · ')
  return (
    <Tooltip title={text}>
      <Box sx={{ display: 'flex', gap: '2px', width: 96, height: 10, borderRadius: 1, overflow: 'hidden', ml: 'auto', transformOrigin: 'left center', ...enter(growX, 700) }} role='img' aria-label={`Pending by age: ${text}`}>
        {counts.map((n, i) => (n ? <Box key={labels[i]} sx={{ flex: n, bgcolor: TONE[bucketTone(i, labels.length)].fg }} /> : null))}
      </Box>
    </Tooltip>
  )
}

const STATUS_FILTERS = [{ key: 'ALL', label: 'All' }, { key: 'ATTN', label: 'Needs attention' }, { key: 'OK', label: 'On track' }]

export default function PodBreakdownTable({ kind, rows = [], bucketLabels = [], onTimeHours, receivedTargetPct = 95, onTimeTargetPct = 90,
  focusedKey, onPick, onEmail, fileLabel }) {
  const lastBucket = bucketLabels.length - 1
  const noun = kind === 'courier' ? 'courier' : 'client'
  const [sort, setSort] = useState({ key: 'pending', dir: 'desc' })
  const [query, setQuery] = useState('')
  const [status, setStatus] = useState('ALL')
  const [via, setVia] = useState('')
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(10)

  // a new tab starts clean
  useEffect(() => { setQuery(''); setStatus('ALL'); setVia(''); setPage(0) }, [kind])
  useEffect(() => { setPage(0) }, [query, status, via, size, rows])

  const flagged = useMemo(() => rows.map((r) => ({ ...r, flags: rowFlags(r, { receivedTargetPct, onTimeTargetPct, lastBucket }) })),
    [rows, receivedTargetPct, onTimeTargetPct, lastBucket])
  const counts = useMemo(() => ({
    ALL: flagged.length,
    ATTN: flagged.filter((r) => r.flags[0].tone !== 'success').length,
    OK: flagged.filter((r) => r.flags[0].tone === 'success').length,
  }), [flagged])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return flagged.filter((r) => (!q || String(r.name || '').toLowerCase().includes(q))
      && (status === 'ALL' || (status === 'OK') === (r.flags[0].tone === 'success'))
      && (!via || (r.podVia || 'MANUAL') === via))
  }, [flagged, query, status, via])

  const sorted = useMemo(() => {
    const v = (r) => (sort.key === 'name' || sort.key === 'podVia' ? String(r[sort.key] || '') : Number(r[sort.key] ?? -1))
    return [...filtered].sort((a, b) => {
      const x = v(a); const y = v(b)
      const c = typeof x === 'string' ? x.localeCompare(y) : x - y
      return sort.dir === 'asc' ? c : -c
    })
  }, [filtered, sort])
  const shown = sorted.slice(page * size, page * size + size)
  const sortBy = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }))
  const head = (key, label, { num: isNum, tip } = {}) => (
    <TableCell key={key} align={isNum ? 'right' : 'left'} sx={{ ...tableHeadSx, whiteSpace: 'nowrap' }}>
      <Tooltip title={tip || ''} disableHoverListener={!tip}>
        <TableSortLabel active={sort.key === key} direction={sort.key === key ? sort.dir : 'desc'} onClick={() => sortBy(key)}>{label}</TableSortLabel>
      </Tooltip>
    </TableCell>
  )

  const download = () => saveCsv(`PTL_PoD_by_${noun}${fileLabel ? `_${fileLabel}` : ''}.csv`, [
    kind === 'courier' ? 'Courier' : 'Client', ...(kind === 'courier' ? ['PoD via'] : []), 'Delivered', 'PoD received', 'PoD received %', 'Pending',
    ...bucketLabels.map((l) => `Pending ${l}`), 'Oldest pending (days)', `On time % (${onTimeHours} h)`, 'Avg hours to PoD', 'Via API', 'Via manual', 'Status',
  ], sorted.map((r) => [
    r.name, ...(kind === 'courier' ? [SOURCE_LABEL[r.podVia] || 'Manual'] : []), r.delivered, r.received, r.receivedPct, r.pending,
    ...bucketLabels.map((_l, i) => r.pendingByBucket?.[i] || 0), r.oldestPendingDays, r.onTimePct, r.avgUploadHours, r.receivedApi, r.receivedManual,
    r.flags.map((f) => f.label).join(' · '),
  ]))

  return (
    <Box>
      {/* tools: search, status, PoD via, download */}
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', py: 1.5 }}>
        <TextField size='small' value={query} onChange={(e) => setQuery(e.target.value)} placeholder={`Search ${noun}`} sx={{ width: { xs: '100%', sm: 260 } }}
          inputProps={{ 'aria-label': `Search ${noun}` }}
          InputProps={{
            startAdornment: <InputAdornment position='start'><SearchIcon fontSize='small' /></InputAdornment>,
            endAdornment: query ? <InputAdornment position='end'><IconButton size='small' aria-label='Clear search' onClick={() => setQuery('')}><CloseIcon fontSize='small' /></IconButton></InputAdornment> : null,
          }} />
        <ToggleButtonGroup size='small' exclusive value={status} onChange={(_e, v) => v && setStatus(v)} aria-label='Status'>
          {STATUS_FILTERS.map((f) => (
            <ToggleButton key={f.key} value={f.key} sx={{ textTransform: 'none', px: 1.5 }}>{f.label} ({num(counts[f.key])})</ToggleButton>
          ))}
        </ToggleButtonGroup>
        {kind === 'courier' && (
          <ToggleButtonGroup size='small' exclusive value={via} onChange={(_e, v) => setVia(v ?? '')} aria-label='PoD via'>
            <ToggleButton value='API' sx={{ textTransform: 'none', px: 1.5 }}>API</ToggleButton>
            <ToggleButton value='MANUAL' sx={{ textTransform: 'none', px: 1.5 }}>Manual</ToggleButton>
          </ToggleButtonGroup>
        )}
        <Button size='small' variant='outlined' startIcon={<FileDownloadOutlinedIcon />} onClick={download} disabled={!sorted.length}
          sx={{ textTransform: 'none', ml: { md: 'auto' } }}>
          Download ({num(sorted.length)})
        </Button>
      </Box>

      {!rows.length ? (
        <Typography sx={{ fontSize: 13, color: 'text.secondary', py: 2 }}>No shipments to show.</Typography>
      ) : !sorted.length ? (
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>No {noun} matches{query ? ` "${query.trim()}"` : ''}.</Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1 }}>Check the spelling, or clear the filters to see all {num(rows.length)}.</Typography>
          <Button size='small' onClick={() => { setQuery(''); setStatus('ALL'); setVia('') }} sx={{ textTransform: 'none' }}>Clear filters</Button>
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  {head('name', kind === 'courier' ? 'Courier' : 'Client')}
                  {kind === 'courier' && head('podVia', 'PoD via', { tip: 'Set on the PTL courier master' })}
                  {head('delivered', 'Delivered', { num: true })}
                  {head('receivedPct', 'PoD received', { num: true, tip: `Target ${receivedTargetPct}%` })}
                  {head('pending', 'Pending', { num: true })}
                  <TableCell align='right' sx={{ ...tableHeadSx, whiteSpace: 'nowrap' }}>
                    <Tooltip title={`Pending split by days since delivery: ${bucketLabels.join(', ')}`}><span>Pending by age</span></Tooltip>
                  </TableCell>
                  {head('oldestPendingDays', 'Oldest', { num: true, tip: 'Days since delivery of the oldest shipment still without a PoD' })}
                  {head('onTimePct', 'On time', { num: true, tip: `PoD within ${onTimeHours} h of delivery · target ${onTimeTargetPct}% · average time to PoD is in the download` })}
                  <TableCell sx={{ ...tableHeadSx, whiteSpace: 'nowrap' }}>Status</TableCell>
                  {onEmail && <TableCell sx={tableHeadSx}><Box component='span' sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Email</Box></TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {shown.map((r) => {
                  const focused = focusedKey && r.key === focusedKey
                  return (
                    <TableRow key={r.key || r.name} hover selected={!!focused} onClick={() => onPick(r)} tabIndex={r.key ? 0 : -1}
                      onKeyDown={(e) => { if (e.key === 'Enter') onPick(r) }}
                      sx={{ cursor: r.key ? 'pointer' : 'default', '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 } }}>
                      <TableCell sx={{ fontWeight: 600, minWidth: 140, maxWidth: 240 }}>
                        <Tooltip title={focused ? 'The page shows only this one' : `Show the page for ${r.name}`}>
                          <Box component='span' sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</Box>
                        </Tooltip>
                      </TableCell>
                      {kind === 'courier' && (
                        <TableCell>
                          <Chip size='small' variant='outlined' label={SOURCE_LABEL[r.podVia] || 'Manual'} color={r.podVia === 'API' ? 'primary' : 'default'}
                            sx={{ height: 22, '& .MuiChip-label': { textTransform: 'none' } }} />
                        </TableCell>
                      )}
                      <TableCell align='right'>{num(r.delivered)}</TableCell>
                      <TableCell align='right' sx={{ minWidth: 130 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                          <LinearProgress variant='determinate' value={r.receivedPct || 0} sx={{ width: 56, height: 6, borderRadius: 3 }}
                            color={(r.receivedPct || 0) >= receivedTargetPct ? 'success' : 'error'} aria-hidden />
                          <span style={{ minWidth: 42 }}>{pct(r.receivedPct)}</span>
                        </Box>
                      </TableCell>
                      <TableCell align='right' sx={{ fontWeight: r.pending ? 700 : 400, color: r.pending ? 'error.main' : 'text.secondary' }}>{num(r.pending)}</TableCell>
                      <TableCell align='right'><AgeBar counts={r.pendingByBucket} labels={bucketLabels} /></TableCell>
                      <TableCell align='right' sx={{ whiteSpace: 'nowrap', color: r.oldestPendingDays != null ? 'text.primary' : 'text.disabled' }}>
                        {r.oldestPendingDays == null ? '–' : `${r.oldestPendingDays} d`}
                      </TableCell>
                      <TableCell align='right'>{pct(r.onTimePct)}</TableCell>
                      <TableCell>
                        {/* worst flag shown; the rest in "+N" with a tooltip, so rows stay one line high */}
                        <Tooltip title={r.flags.map((f) => f.label).join(' · ')}>
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', whiteSpace: 'nowrap' }}>
                            <FlagChip {...r.flags[0]} />
                            {r.flags.length > 1 && <Typography component='span' sx={{ fontSize: 12, color: 'text.secondary' }}>+{r.flags.length - 1}</Typography>}
                          </Box>
                        </Tooltip>
                      </TableCell>
                      {onEmail && (
                        <TableCell align='right' onClick={(e) => e.stopPropagation()}>
                          {r.key && r.pending > 0 && (
                            <Tooltip title={`Email ${r.name} its pending PoDs`}>
                              <IconButton size='small' aria-label={`Email ${r.name} its pending PoDs`} onClick={() => onEmail(r)}><MailOutlineIcon fontSize='small' /></IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component='div' count={sorted.length} page={page} onPageChange={(_e, p) => setPage(p)} rowsPerPage={size}
            onRowsPerPageChange={(e) => setSize(parseInt(e.target.value, 10))} rowsPerPageOptions={[10, 25, 50, 100]}
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${num(count)} ${noun}s`} />
        </>
      )}
    </Box>
  )
}
